// Per-user risk score breakdown modal. Surfaces every signal's component
// value, weight, anchor (industry / platform / target) and contribution
// to the final score. Opened from the user table, user detail header
// and AI-summary cards.
//
// Calculations: thin wrapper over riskScore.computeBreakdown(email).
//
// Postgres mapping: read `user_risk_scores.signals_json` so the modal
// does not re-run the model client-side. Anchors come from
// `industry_benchmarks` joined by factor_id.
//
// Integration hook: `useUserRiskQuery(email)` cache 1h. Methodology
// link drills into RiskMethodologyPage.
import React from 'react';
import { Box, Typography, Dialog, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { computeBreakdown, formatAnchor } from '../riskScore';
import useFeature from '../useFeature';

function Bar({ value, color }) {
    return (
        <Box sx={{ height: 6, borderRadius: '3px', bgcolor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.min(100, Math.max(0, value))}%`, bgcolor: color, borderRadius: '3px', transition: 'width .4s ease' }} />
        </Box>
    );
}

// Server signal vocabulary: mirrors refresh.py's v2 model exactly - weights
// 35/20/20/15/10 and the n/(n+3) shrink toward the 0.5 neutral baseline. The
// modal recomputes the shrunk components from the stored raw signals so the
// contributions reconcile with the server's base score.
const SHRINK_K = 3;
const SERVER_SIGNALS = [
    { id: 'failRate',     label: 'Fail rate',       weight: 35, count: 'sims' },
    { id: 'repeatRate',   label: 'Repeat offender', weight: 20, count: 'sims' },
    { id: 'stageDepth',   label: 'Stage depth',     weight: 20, count: 'sims' },
    { id: 'trainingDebt', label: 'Training debt',   weight: 15, count: 'courses' },
    { id: 'courseLag',    label: 'Course lag',      weight: 10, count: 'courses' },
];
export const BAND_META = {
    Low:      { color: '#66BB6A', bg: 'rgba(102,187,106,.12)' },
    Medium:   { color: '#FFCA28', bg: 'rgba(255,202,40,.12)' },
    High:     { color: '#FF7043', bg: 'rgba(255,112,67,.12)' },
    Critical: { color: '#FF5252', bg: 'rgba(255,82,82,.12)' },
};
const shrunk = (v, m) => {
    const w = m > 0 ? m / (m + SHRINK_K) : 0;
    return (v == null ? 0.5 : v) * w + 0.5 * (1 - w);
};

// Renders the SERVER model (risk_snapshot signals passed via serverFactors)
// so the breakdown always reconciles with the roster's score - the client
// model can't reproduce the server's day-over-day EWMA smoothing.
function ServerBreakdown({ factors }) {
    const band = BAND_META[factors.band] || BAND_META.Medium;
    const simsN = factors.simsInWindow ?? 0;
    const coursesN = factors.coursesSent ?? 0;
    const rows = SERVER_SIGNALS.map((s) => {
        const m = s.count === 'sims' ? simsN : coursesN;
        const component = shrunk(factors[s.id], m);
        return { ...s, raw: factors[s.id], component, contribution: s.weight * component };
    });
    return (
        <>
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.25 }}>Displayed score</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography sx={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: band.color }}>
                            {factors.finalScore}
                        </Typography>
                        <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 700, bgcolor: band.bg, color: band.color }}>
                            {factors.band}
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ height: 36, width: '1px', bgcolor: 'divider' }} />
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.25 }}>Today's base score</Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>{(factors.baseScore ?? 0).toFixed(1)}</Box>
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.25 }}>
                        Displayed score smooths daily base scores (4-day half-life), so it trails today's base.
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {rows.map((s) => {
                    const componentScore = Math.round(s.component * 100);
                    const colorBand = componentScore >= 76 ? '#FF5252' : componentScore >= 56 ? '#FF7043' : componentScore >= 30 ? '#FFCA28' : '#66BB6A';
                    return (
                        <Box key={s.id}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.6, gap: 2 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>{s.label}</Typography>
                                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'baseline' }}>
                                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>Weight {s.weight}%</Typography>
                                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>Score <Box component="span" sx={{ fontWeight: 700, color: colorBand }}>{componentScore}</Box></Typography>
                                </Box>
                            </Box>
                            <Bar value={componentScore} color={colorBand} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.6, alignItems: 'baseline', gap: 2 }}>
                                <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                    {s.raw == null
                                        ? 'No data - neutral 0.5 baseline'
                                        : `Measured ${(s.raw * 100).toFixed(0)}%, eased toward 0.5 by sample size (${s.count === 'sims' ? `${simsN} sims` : `${coursesN} courses`})`}
                                </Typography>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.primary' }}>
                                    {s.contribution.toFixed(1)} score pts
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    Sum of contributions: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{rows.reduce((a, x) => a + x.contribution, 0).toFixed(1)}</Box>
                    {' '}= today's base -&gt; smoothed to <Box component="span" sx={{ fontWeight: 700, color: band.color }}>{factors.finalScore}</Box>
                </Typography>
            </Box>
        </>
    );
}

export default function RiskBreakdownModal({ open, onClose, email, name, dept, go, serverFactors }) {
    const useServer = !!(serverFactors && serverFactors.finalScore != null);
    const breakdown = !useServer && email ? computeBreakdown(email) : null;
    const insufficient = !useServer && (!breakdown || breakdown.insufficient);
    const benchmarkOn = useFeature('industryBenchmarking');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: 'background.paper',
                    border: '1px solid', borderColor: 'divider',
                    borderRadius: '10px',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
                },
            }}
        >
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>Risk score breakdown</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', mt: 0.25 }}>{name || email}</Typography>
                    {dept && <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>{dept}</Typography>}
                    <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.75 }}>
                        Rolling 180-day (6-month) metric — not re-windowed by any selected date range.
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: 'text.disabled' }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            {useServer ? (
                <ServerBreakdown factors={serverFactors} />
            ) : insufficient ? (
                <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                        Insufficient sim data to score this user.
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        A score requires at least 3 simulations delivered in the last 6 months.
                        This user has {breakdown?.simsInWindow ?? 0}.
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.25 }}>Final</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography sx={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: breakdown.band.color }}>
                                    {breakdown.finalScore}
                                </Typography>
                                <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 700, bgcolor: breakdown.band.bg, color: breakdown.band.textColor }}>
                                    {breakdown.band.name}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ height: 36, width: '1px', bgcolor: 'divider' }} />
                        <Box>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.25 }}>Base x Multiplier</Typography>
                            <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                                <Box component="span" sx={{ fontWeight: 700 }}>{breakdown.baseScore.toFixed(1)}</Box>
                                {' x '}
                                <Box component="span" sx={{ fontWeight: 700, color: 'secondary.main' }}>{breakdown.multiplier.toFixed(2)}</Box>
                                {' = '}
                                <Box component="span" sx={{ fontWeight: 700 }}>{(breakdown.baseScore * breakdown.multiplier).toFixed(1)}</Box>
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.25 }}>
                                Multiplier from previous band: <Box component="span" sx={{ color: breakdown.previousBand.textColor, fontWeight: 600 }}>{breakdown.previousBand.name}</Box>
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {breakdown.signals.map((s) => {
                            const componentScore = Math.round(s.component * 100);
                            const colorBand = componentScore >= 76 ? '#FF5252' : componentScore >= 56 ? '#FF7043' : componentScore >= 30 ? '#FFCA28' : '#66BB6A';
                            // "vs industry" copy only appears when benchmarking is on for
                            // this tenancy. Without it, fall back to the base x multiplier
                            // breakdown so the row still reconciles the maths.
                            const anchorText = benchmarkOn ? formatAnchor(s.anchor, s.component) : null;
                            const aboveIndustry = benchmarkOn && s.anchor && s.anchor.goodBelow && s.component > s.anchor.industry;
                            return (
                                <Box key={s.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.6, gap: 2 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>{s.label}</Typography>
                                        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'baseline' }}>
                                            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>Weight {s.weight}%</Typography>
                                            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>Score <Box component="span" sx={{ fontWeight: 700, color: colorBand }}>{componentScore}</Box></Typography>
                                        </Box>
                                    </Box>
                                    <Bar value={componentScore} color={colorBand} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.6, alignItems: 'baseline', gap: 2 }}>
                                        <Typography sx={{ fontSize: 10, color: aboveIndustry ? '#FF5252' : 'text.secondary' }}>
                                            {anchorText || `Base ${s.baseContribution.toFixed(1)} x ${breakdown.multiplier.toFixed(2)}`}
                                        </Typography>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.primary' }}>
                                            {s.finalContribution.toFixed(1)} score pts
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            Sum of contributions: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{breakdown.signals.reduce((s, x) => s + x.finalContribution, 0).toFixed(1)}</Box>
                            {' '}-> clamped to <Box component="span" sx={{ fontWeight: 700, color: breakdown.band.color }}>{breakdown.finalScore}</Box>
                        </Typography>
                    </Box>
                </>
            )}

            <Box sx={{ px: 3, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                    size="small"
                    onClick={() => { onClose(); go && go('risk-methodology', 'Risk methodology'); }}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: 11, textTransform: 'none', color: 'secondary.main', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                >
                    How is this calculated?
                </Button>
                <Button size="small" variant="contained" onClick={onClose} sx={{ fontSize: 11, textTransform: 'none' }}>Close</Button>
            </Box>
        </Dialog>
    );
}
